
export function LicensingProcess() {
  const steps = [
    {
      number: '01',
      title: 'Obtain Provincial Registry Number',
      description:
        'First, register with BC\'s Provincial STR Registry. This is mandatory before applying for Vancouver city license. Platforms verify registry numbers and automatically de-list properties without valid registration.',
      timeline: '1-2 weeks',
      documents: [
        'Government-issued photo ID',
        'Proof of property ownership OR long-term lease',
        'Principal residence documentation (183+ days/year)',
        'Provincial Registry application',
      ],
    },
    {
      number: '02',
      title: 'Verify Eligibility & Get Authorization',
      description:
        'Confirm your property meets all requirements: principal residence status (183+ days/year), zoning compliance, and owner-occupant criteria. Tenant-hosts must obtain written landlord consent using City\'s official form AND formal Strata Council authorization letter.',
      timeline: '1-3 weeks',
      documents: [
        'Property title OR lease agreement',
        'Utility bills proving residence (183+ days)',
        'Landlord Consent Form (if tenant)',
        'Strata authorization letter (if condo tenant)',
      ],
    },
    {
      number: '03',
      title: 'Prepare Fire Safety Plan',
      description:
        'Create a Fire Safety Plan with visual floor plan showing emergency exits. Ensure certified fire extinguishers are installed. This is mandatory for all Vancouver STRs in 2026.',
      timeline: '3-5 days',
      documents: [
        'Visual floor plan with exits marked',
        'Fire extinguisher certification',
        'Smoke and CO detector documentation',
        'Emergency contact information',
      ],
    },
    {
      number: '04',
      title: 'Submit City Application',
      description:
        'Complete the online application through Vancouver\'s business license portal. Include your Provincial Registry Number, Fire Safety Plan, and all required documentation. Pay the application fee ($77) and annual license fee ($1,108).',
      timeline: '1 day',
      documents: [
        'Provincial Registry Number (mandatory)',
        'Completed application form',
        'Fire Safety Plan',
        'Payment: $77 application + $1,108 annual license',
      ],
    },
    {
      number: '05',
      title: 'City Review Process',
      description:
        'Vancouver staff review your application, verify Provincial Registry status, landlord/strata authorization (if tenant), and may conduct property inspections. Response time varies based on application volume.',
      timeline: '4-8 weeks',
      documents: [
        'Await city inspection (if required)',
        'Respond to any information requests',
        'Address any compliance issues',
        'Final approval notification',
      ],
    },
    {
      number: '06',
      title: 'Receive License & List',
      description:
        'Once approved, receive your business license number. Display both your Provincial Registry Number and city license number prominently in all online listings and begin operating legally.',
      timeline: '1-2 days',
      documents: [
        'Business license certificate',
        'Provincial Registry Number',
        'Both license numbers for listings',
        'Annual renewal reminder',
      ],
    },
  ];

  return (
    <section id="licensing" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Vancouver 2026 STR Licensing Process
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Follow these steps to obtain your Provincial Registry Number and Vancouver business license. The process now requires provincial registration first, then city licensing. Tenant-hosts need additional landlord and strata authorization. Total timeline: 8-14 weeks.
          </p>
        </div>

        <div className="space-y-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-[#F0F0F0] rounded-2xl p-8 lg:p-10 hover:shadow-lg transition-all"
            >
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-[#882629] rounded-xl flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">{step.number}</span>
                  </div>
                </div>

                <div className="flex-grow">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                    <h3 className="text-2xl font-semibold text-[#505A5A] mb-2 lg:mb-0">
                      {step.title}
                    </h3>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-[#882629] bg-white px-4 py-2 rounded-full whitespace-nowrap w-fit">
                      <i className="ri-time-line"></i>
                      {step.timeline}
                    </span>
                  </div>

                  <p className="text-base text-[#505A5A]/70 mb-6 leading-relaxed">
                    {step.description}
                  </p>

                  <div className="bg-white rounded-xl p-6">
                    <h4 className="text-sm font-semibold text-[#505A5A] mb-4 uppercase tracking-wide">
                      Required Documents:
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {step.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <i className="ri-file-text-line text-[#882629] text-lg mt-0.5 flex-shrink-0"></i>
                          <span className="text-sm text-[#505A5A]/80">{doc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-[#882629]/10 px-6 py-4 rounded-full">
            <i className="ri-lightbulb-line text-2xl text-[#882629]"></i>
            <p className="text-base text-[#505A5A] font-medium">
              We handle the entire licensing process — Provincial Registry, city license, and tenant authorization
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
